import type { MetricAlert } from "@/features/dashboard/shell/types";

import type { IMeddreamHourlyMetric } from "../schemas/meddream-hourly";

import { findMetricGaps } from "./aggregate";

const MAX_HOURS_SHOWN = 4;

/** Lista compacta de horas UTC (ids) para descripciones agrupadas. */
function formatHourList(ids: string[], max = MAX_HOURS_SHOWN): string {
  if (ids.length === 0) return "";
  const shown = ids.slice(0, max);
  const suffix = ids.length > max ? ` … (+${ids.length - max})` : "";
  return `${shown.join(", ")}${suffix}`;
}

/**
 * Reglas de alertas/insights en UI, agrupadas por tipo (una fila por categoría).
 */
export function buildMeddreamAlerts(
  metrics: IMeddreamHourlyMetric[],
  startMs: number,
  endMs: number
): MetricAlert[] {
  const alerts: MetricAlert[] = [];

  const restartHours: { id: string; count: number }[] = [];
  let restartEvents = 0;
  for (const m of metrics) {
    if (m.restartsCount >= 1) {
      restartHours.push({ id: m.id, count: m.restartsCount });
      restartEvents += m.restartsCount;
    }
  }
  if (restartHours.length > 0) {
    const hours = restartHours.map((h) => h.id);
    alerts.push({
      id: "restarts",
      severity: "warning",
      title:
        restartHours.length === 1
          ? "Reinicio del servicio detectado"
          : `Reinicios del servicio (${restartHours.length} h)`,
      description:
        restartHours.length === 1
          ? `${restartEvents} evento(s) SystemInfo (arranque/reinicio) en ${hours[0]} (UTC).`
          : `${restartEvents} evento(s) SystemInfo en ${restartHours.length} hora(s): ${formatHourList(hours)} (UTC).`,
      hourKey: restartHours[0]?.id,
    });
  }

  const dropHours: { id: string; from: number }[] = [];
  for (let i = 1; i < metrics.length; i++) {
    const prev = metrics[i - 1];
    const curr = metrics[i];
    if (prev.activeConnectionsMax >= 3 && curr.activeConnectionsMax === 0) {
      dropHours.push({ id: curr.id, from: prev.activeConnectionsMax });
    }
  }
  if (dropHours.length > 0) {
    const hours = dropHours.map((h) => h.id);
    const peakHint =
      dropHours.length === 1
        ? `Pico ${dropHours[0].from} → 0`
        : `${dropHours.length} caídas (pico≥3 → 0)`;
    alerts.push({
      id: "drops",
      severity: "critical",
      title:
        dropHours.length === 1
          ? "Posible caída de conexiones"
          : `Posibles caídas de conexiones (${dropHours.length})`,
      description: `${peakHint} en ${formatHourList(hours)} (UTC).`,
      hourKey: dropHours[0]?.id,
    });
  }

  const gaps = findMetricGaps(metrics, startMs, endMs);
  if (gaps.length > 0) {
    const show = Math.min(gaps.length, MAX_HOURS_SHOWN);
    const samples = gaps
      .slice(0, show)
      .map((ms) => new Date(ms).toISOString().slice(0, 13))
      .join(", ");
    alerts.push({
      id: "gaps",
      severity: gaps.length > 6 ? "warning" : "info",
      title: "Huecos sin datos",
      description: `${gaps.length} hora(s) sin documento en el rango (ej. ${samples}${gaps.length > show ? "…" : ""}).`,
    });
  }

  const lowSampleHours: { id: string; samples: number; neighborAvg: number }[] =
    [];
  for (let i = 0; i < metrics.length; i++) {
    const m = metrics[i];
    const neighbors: number[] = [];
    if (i > 0) neighbors.push(metrics[i - 1].samplesCount);
    if (i < metrics.length - 1) neighbors.push(metrics[i + 1].samplesCount);
    if (neighbors.length === 0) continue;
    const neighborAvg = neighbors.reduce((a, b) => a + b, 0) / neighbors.length;
    if (
      neighborAvg >= 10 &&
      m.samplesCount > 0 &&
      m.samplesCount < neighborAvg * 0.25
    ) {
      lowSampleHours.push({
        id: m.id,
        samples: m.samplesCount,
        neighborAvg: Math.round(neighborAvg),
      });
    }
  }
  if (lowSampleHours.length > 0) {
    const hours = lowSampleHours.map((h) => h.id);
    const detail =
      lowSampleHours.length === 1
        ? `${lowSampleHours[0].samples} mediciones del log vs ~${lowSampleHours[0].neighborAvg} en horas vecinas (${hours[0]} UTC). El promedio de esa hora es poco representativo.`
        : `${lowSampleHours.length} hora(s) con pocas mediciones del log vs vecinas: ${formatHourList(hours)}.`;
    alerts.push({
      id: "low-samples",
      severity: "info",
      title:
        lowSampleHours.length === 1
          ? "Pocas mediciones del log"
          : `Pocas mediciones del log (${lowSampleHours.length} h)`,
      description: detail,
      hourKey: lowSampleHours[0]?.id,
    });
  }

  return alerts;
}
